import { action } from '@ember/object';
import didUpdate from '@ember/render-modifiers/modifiers/did-update';
import { isPresent } from '@ember/utils';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import eq from 'ember-truth-helpers/helpers/eq';
import moment from 'moment-timezone';
import BvDateRangeField from 'volta/components/bv-date-range-field';
import BvInline from 'volta/components/bv-inline';
import DynamicDateFilter from 'volta/components/filters/dynamic-date-filter';
import { DynamicDatePrefix } from 'volta/models/constants/date-filters';
import { formatISODate, LOCAL_DATE_FORMAT } from 'volta/utils/date-utils';

import type { TDate } from 'volta/models/types/calendar';

interface FiltersDateFilterComponentArgs {}

export interface FiltersDateFilterSignature
  extends FilterComponentSignature<FiltersDateFilterComponentArgs> {
  Element: HTMLElement;
}

export default class FiltersDateFilterComponent extends Component<FiltersDateFilterSignature> {
  @tracked left?: string = this.values?.[0];
  @tracked right?: string = this.values?.[1];
  @tracked dynamicValue?: string;

  placeholder = moment.localeData().longDateFormat(LOCAL_DATE_FORMAT);

  get values() {
    return this.args.values ?? [];
  }

  get leftValue() {
    return this.dynamicValue ?? this.left ?? this.value;
  }

  get value() {
    return this.values?.length ? this.values[0] : undefined;
  }

  get rightValue() {
    return this.right ?? (this.values?.length === 2 ? this.values[1] : undefined);
  }

  get dateFilter() {
    return {
      startDate: isPresent(this.left) ? moment.utc(this.left).toDate() : undefined,
      endDate: isPresent(this.right) ? moment.utc(this.right).toDate() : undefined
    };
  }

  @action
  valuesDidChange({ startDate, endDate }: { startDate?: TDate; endDate?: TDate }) {
    const values: FiltersDateFilterSignature['Args']['values'] = [];
    if (startDate) {
      values.push(this.formatValue(startDate)!);
    }
    if (endDate) {
      values.push(this.formatValue(endDate)!);
    }

    if (values.length === 2) {
      this.args.onChange?.(values);
    }
  }

  @action
  valueDidChange(isFromCalendar: boolean, newValue?: Date | string) {
    const values: FiltersDateFilterSignature['Args']['values'] = isPresent(newValue)
      ? newValue instanceof Date
        ? [this.formatValue(newValue) as string]
        : [newValue as string]
      : [];
    this.args.onChange?.(values);

    if (isFromCalendar) {
      this.dynamicValue = undefined;
    }
  }

  @action
  handleUpdateValues(
    _el: HTMLElement,
    [[left, right] = [undefined, undefined]]: [[string | undefined, string | undefined]]
  ) {
    if (this.left !== left) {
      this.left = left;
      if (this.left?.startsWith(DynamicDatePrefix) || this.left === undefined) {
        this.dynamicValue = this.left;
      }
    }
    if (this.right !== right) {
      this.right = right;
    }
  }

  private formatValue(value?: TDate) {
    return isPresent(value) ? formatISODate(value) : undefined;
  }

  @action
  handleDynamicValue(value: string) {
    this.dynamicValue = value;
    this.valueDidChange(false, value);
  }

  <template>
    {{! template-lint-disable no-at-ember-render-modifiers }}
    <BvInline
      @gap='1'
      @blockAlign='center'
      @wrap={{false}}
      class='u-full-width'
      {{! @glint-ignore }}
      {{didUpdate this.handleUpdateValues @values}}
    >
      {{#if (eq @comparator 'bt')}}
        <BvDateRangeField
          @dateFilter={{this.dateFilter}}
          @onChange={{this.valuesDidChange}}
          @horizontalPosition='auto-right'
          @allowClear={{false}}
          @startDatePlaceholder={{this.placeholder}}
          @endDatePlaceholder={{this.placeholder}}
          ...attributes
        />
      {{else}}
        <DynamicDateFilter
          @name={{@name}}
          @valueDidChange={{this.valueDidChange}}
          {{! @glint-ignore }}
          @value={{this.leftValue}}
          @handleDynamicValue={{this.handleDynamicValue}}
          @dynamicValue={{this.dynamicValue}}
        />
      {{/if}}
    </BvInline>
  </template>
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Filters::DateFilter': typeof FiltersDateFilterComponent;
    'filters/date-filter': typeof FiltersDateFilterComponent;
  }
}
